<script lang="ts">
  // Lifecycle Status sub-panel per INTERFACE-SPECIFICATION.md §10.6.
  //
  // Snapshot of the governance system's operational health. Reads
  // what is reachable from the maintenance interface today:
  //   - Schema version (from the manifest)
  //   - Dimension catalogue tables (bundled)
  //   - Worker source (from the catalogue meta on the next read)
  //   - Authorisation summary (from access-control)
  //   - Substantiation health (orphan count from the cubes)
  // The CODEOWNERS sync state, scenario-suite pass rate, and last
  // deploy timestamp come from CI logs; placeholders surface them
  // until a Worker-side health endpoint is added.

  import { onMount } from 'svelte';
  import { bootstrapManifest, catalogueState, fetchCatalogue } from '../../lib/catalogue-store.svelte';
  import { accessControlState, loadAccessControlSnapshot } from '../../lib/access-control-store.svelte';
  import { PRODUCTS } from '../../lib/products';
  import { dimensions } from '../../lib/dimensions';
  import { formatLocalDate } from '../../lib/formatters';

  onMount(async () => {
    if (!catalogueState.manifest) await bootstrapManifest();
    await loadAccessControlSnapshot();
    await Promise.all(PRODUCTS.map((p) => fetchCatalogue(p.jurisdiction, p.product)));
  });

  const orphanCount = $derived(
    PRODUCTS.reduce((acc, p) => {
      const cube = catalogueState.catalogues[p.jurisdiction + '/' + p.product]?.catalogue;
      const gov = (cube?.product as { _governance?: { substantiation_source?: string } } | undefined)?._governance;
      return acc + (gov?.substantiation_source ? 0 : 1);
    }, 0)
  );

  const adminCount = $derived(accessControlState.users.filter((u) => u.roles.includes('admin')).length);
</script>

<section>
  <h3 class="section-heading">Lifecycle status</h3>

  <h4>Schema</h4>
  <ul class="status-list">
    <li><span class="muted">Schema version</span><span><code>1.0.0</code></span></li>
  </ul>

  <h4>Dimension catalogue</h4>
  <ul class="status-list">
    {#each PRODUCTS as p (p.jurisdiction + p.product)}
      {@const cube = catalogueState.catalogues[p.jurisdiction + '/' + p.product]?.catalogue}
      <li>
        <span>{p.label}</span>
        <span class="muted"><code>{cube?._dimension_refs.bureau_band_table ?? cube?._dimension_refs.grade_subgrade_table ?? '—'}</code></span>
      </li>
    {/each}
  </ul>

  <h4>Authorisation</h4>
  <ul class="status-list">
    <li><span class="muted">Authorised users</span><span>{accessControlState.users.length}</span></li>
    <li><span class="muted">Admins</span><span>{adminCount}</span></li>
  </ul>

  <h4>Substantiation health</h4>
  <ul class="status-list">
    <li><span class="muted">Orphaned values</span><span class={orphanCount === 0 ? 'status-pass' : 'status-warn'}>{orphanCount}</span></li>
  </ul>

  <h4>Operational signals (deferred to PR 5)</h4>
  <ul class="status-list">
    <li><span class="muted">CODEOWNERS sync</span><span class="muted">Verified by validate.yml on every PR</span></li>
    <li><span class="muted">Scenario suite</span><span class="muted">Surfaced via Scenarios Index sub-panel</span></li>
    <li><span class="muted">Last successful deploy</span><span class="muted">CI logs in GitHub Actions</span></li>
  </ul>

  <p class="muted">Dimension tables bundled: {dimensions.pricingModels.length} pricing models, {dimensions.bureauBandTables.length} band tables, {dimensions.bureaus.length} bureaus.</p>
</section>

<style>
  .status-list {
    list-style: none;
    padding: 0;
    margin: 0 0 var(--space-3);
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }
  .status-list li {
    display: flex;
    justify-content: space-between;
    padding: var(--space-1) 0;
    border-bottom: 1px solid var(--surface-line-soft);
  }
  h4 { margin: var(--space-3) 0 var(--space-1); font-size: var(--type-md); font-weight: 500; }
  code { font-family: var(--font-mono); font-size: 0.9em; }
</style>
